import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Auth helpers
export const signUp = async (email: string, password: string, fullName: string) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName,
      },
    },
  });
  return { data, error };
};

export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Profile helpers
export const getProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  return { data, error };
};

export const updateProfile = async (userId: string, updates: any) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId);
  return { data, error };
};

// Wishlist helpers
export const getWishlist = async (userId: string) => {
  const { data, error } = await supabase
    .from('wishlist')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  return { data, error };
};

export const addToWishlist = async (userId: string, product: any) => {
  const { data, error } = await supabase
    .from('wishlist')
    .insert({
      user_id: userId,
      product_id: product.id,
      product_name: product.name,
      product_image: product.image,
      product_price: product.price,
    });
  return { data, error };
};

export const removeFromWishlist = async (wishlistId: string) => {
  const { error } = await supabase
    .from('wishlist')
    .delete()
    .eq('id', wishlistId);
  return { error };
};

// Orders helpers
export const getOrders = async (userId: string) => {
  const { data, error } = await supabase
    .from('orders')
    .select(`
      *,
      order_items (*)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  return { data, error };
};

// Reviews helpers
export const getReviews = async (productId: string) => {
  const { data, error } = await supabase
    .from('reviews')
    .select(`
      *,
      profiles (full_name, avatar_url)
    `)
    .eq('product_id', productId)
    .order('created_at', { ascending: false });
  return { data, error };
};

export const getReviewStats = async (productId: string) => {
  const { data, error } = await supabase
    .from('review_stats')
    .select('*')
    .eq('product_id', productId)
    .single();
  return { data, error };
};

export const createReview = async (review: {
  user_id: string;
  product_id: string;
  rating: number;
  title: string;
  content: string;
  images?: string[];
}) => {
  const { data, error } = await supabase
    .from('reviews')
    .insert(review);
  return { data, error };
};

export const updateReview = async (reviewId: string, updates: any) => {
  const { data, error } = await supabase
    .from('reviews')
    .update(updates)
    .eq('id', reviewId);
  return { data, error };
};

export const deleteReview = async (reviewId: string) => {
  const { error } = await supabase
    .from('reviews')
    .delete()
    .eq('id', reviewId);
  return { error };
};

// Cart helpers
export const getCart = async (userId: string) => {
  const { data, error } = await supabase
    .from('cart')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  return { data, error };
};

export const addToCart = async (userId: string, product: any, quantity: number = 1) => {
  // 먼저 장바구니에 이미 있는지 확인
  const { data: existing } = await supabase
    .from('cart')
    .select('*')
    .eq('user_id', userId)
    .eq('product_id', product.id)
    .single();

  if (existing) {
    // 이미 있으면 수량 업데이트
    const { data, error } = await supabase
      .from('cart')
      .update({ quantity: existing.quantity + quantity })
      .eq('id', existing.id);
    return { data, error };
  } else {
    // 없으면 새로 추가
    const { data, error } = await supabase
      .from('cart')
      .insert({
        user_id: userId,
        product_id: product.id,
        product_name: product.name,
        product_image: product.images[0],
        product_price: product.price,
        quantity,
      });
    return { data, error };
  }
};

export const updateCartQuantity = async (cartId: string, quantity: number) => {
  const { data, error } = await supabase
    .from('cart')
    .update({ quantity })
    .eq('id', cartId);
  return { data, error };
};

export const removeFromCart = async (cartId: string) => {
  const { error } = await supabase
    .from('cart')
    .delete()
    .eq('id', cartId);
  return { error };
};

export const clearCart = async (userId: string) => {
  const { error } = await supabase
    .from('cart')
    .delete()
    .eq('user_id', userId);
  return { error };
};
